import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CERTIFICATE_PREVIEW_LABELS } from '../../constants/labels';
import type {
  CertificateTemplateDetail,
  CertificateTemplateSelectedFormField,
} from '../../types';
import {
  CertificateIntro,
  EquipmentSectionTitle,
  EquipmentDetailCard,
  FormFieldsCard,
} from './CertificateContent';
import { CertificateSignatures } from './CertificateLayout';
import {
  BLOCK_GAP,
  CONTENT_WIDTH,
  FIELDS_PER_CARD,
  FIELD_CARD_HEIGHT,
  FRAME_PADDING_Y,
  LOGO_GAP,
  PAGE_HEIGHT,
  PAPER_PADDING,
  CertificatePage,
} from './CertificatePage';
import {
  buildCertificatePages,
  chunkFields,
  type CertificateBlock,
  type CertificateBlockInput,
  type CertificatePageModel,
} from './certificatePagination';

interface CertificatePreviewProps {
  template: CertificateTemplateDetail;
  /** Override the outer wrapper className. Defaults to flex-1 min-h-0 overflow-auto (for constrained-height views like ViewCertificateTemplate). Pass a plain width/bg class when the parent scroll container should handle scrolling (e.g. inside CreateLayout). */
  className?: string;
}

export const CertificatePreview = ({
  template,
  className = 'flex-1 min-h-0 w-full overflow-auto bg-white',
}: CertificatePreviewProps) => {
  const headerText = template.headerField?.trim() || template.certTemplateName;
  const subtitleText =
    template.subtitle?.trim() || CERTIFICATE_PREVIEW_LABELS.SUBTITLE_DEFAULT;
  const footerText = template.footerField?.trim() ?? '';
  const formFields: CertificateTemplateSelectedFormField[] = useMemo(
    () => template.selectedFormFields ?? [],
    [template.selectedFormFields]
  );
  const { includeTechnicianDetail } = template;

  // Form fields are grouped into fixed-height cards of up to 6 subfields each;
  // a card that doesn't fit the current page flows onto the next one.
  const fieldCards = useMemo(
    () => chunkFields(formFields, FIELDS_PER_CARD),
    [formFields]
  );

  // A re-measure is triggered whenever anything that affects the variable block
  // heights (header/subtitle/footer text, technician flag) or the card count
  // changes.
  const signature = useMemo(
    () =>
      JSON.stringify({
        headerText,
        subtitleText,
        footerText,
        includeTechnicianDetail,
        cardCount: fieldCards.length,
      }),
    [headerText, subtitleText, footerText, includeTechnicianDetail, fieldCards]
  );

  const [pageState, setPageState] = useState<{
    signature: string;
    pages: CertificatePageModel[];
  } | null>(null);
  // Non-null only when the current content has been measured & paginated.
  const pages =
    pageState && pageState.signature === signature ? pageState.pages : null;

  const logoRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const equipTitleRef = useRef<HTMLDivElement>(null);
  const signaturesRef = useRef<HTMLDivElement>(null);

  const buildBlocks = (
    introHeight: number,
    equipTitleHeight: number
  ): CertificateBlockInput[] => [
    { block: { kind: 'intro' }, height: introHeight },
    { block: { kind: 'equipTitle' }, height: equipTitleHeight },
    { block: { kind: 'equipCard' }, height: FIELD_CARD_HEIGHT },
    ...fieldCards.map((fields) => ({
      block: { kind: 'fields' as const, fields },
      height: FIELD_CARD_HEIGHT,
    })),
  ];

  useLayoutEffect(() => {
    if (pages) {
      return;
    }
    const h = (ref: React.RefObject<HTMLElement | null>) =>
      ref.current?.offsetHeight ?? 0;

    const logoHeight = h(logoRef);
    const capacity =
      PAGE_HEIGHT -
      2 * PAPER_PADDING -
      2 * FRAME_PADDING_Y -
      logoHeight -
      LOGO_GAP;

    const blocks = buildBlocks(h(introRef), h(equipTitleRef));

    let nextPages: CertificatePageModel[];
    if (!Number.isFinite(capacity) || capacity <= 0) {
      // No usable measurement (e.g. jsdom / 0-height) → single page.
      nextPages = [
        { blocks: blocks.map((b) => b.block), showSignatures: true },
      ];
    } else {
      nextPages = buildCertificatePages(blocks, {
        capacity,
        gap: BLOCK_GAP,
        signaturesHeight: h(signaturesRef),
      });
    }

    setPageState({ signature, pages: nextPages });
    // buildBlocks closes over fieldCards, which `signature` already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, pages]);

  const renderBlock = (block: CertificateBlock, key: number) => {
    switch (block.kind) {
      case 'intro':
        return (
          <CertificateIntro
            key={key}
            headerText={headerText}
            subtitleText={subtitleText}
          />
        );
      case 'equipTitle':
        return <EquipmentSectionTitle key={key} />;
      case 'equipCard':
        return (
          <EquipmentDetailCard key={key} fixedHeight={FIELD_CARD_HEIGHT} />
        );
      case 'fields':
        return (
          <FormFieldsCard
            key={key}
            fields={block.fields}
            fixedHeight={FIELD_CARD_HEIGHT}
          />
        );
    }
  };

  return (
    <div className={className}>
      {!pages ? (
        // Hidden measurement layer — rendered once at the real content width so
        // we can read the variable block heights (logo, intro, section title,
        // signatures); cards use a fixed height. Replaced by the pages below.
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            top: 0,
            left: 0,
            width: CONTENT_WIDTH,
          }}
        >
          <div ref={logoRef} className="flex justify-center">
            <img
              src="/pozytronLogo.svg"
              alt=""
              className="w-[290px] h-[68px] shrink-0"
            />
          </div>
          <div ref={introRef}>
            <CertificateIntro
              headerText={headerText}
              subtitleText={subtitleText}
            />
          </div>
          <div ref={equipTitleRef}>
            <EquipmentSectionTitle />
          </div>
          <div ref={signaturesRef}>
            <CertificateSignatures
              includeTechnicianDetail={includeTechnicianDetail}
              footerText={footerText}
            />
          </div>
        </div>
      ) : (
        <div className="min-w-max flex flex-col items-center gap-8">
          {pages.map((p, pageIndex) => (
            <CertificatePage
              key={pageIndex}
              includeTechnicianDetail={includeTechnicianDetail}
              footerText={footerText}
              showSignatures={p.showSignatures}
            >
              {p.blocks.map((block, blockIndex) =>
                renderBlock(block, blockIndex)
              )}
            </CertificatePage>
          ))}
        </div>
      )}
    </div>
  );
};
